import React, { useMemo } from 'react';
import { Column, Row } from 'react-table';
import { maxBy } from 'lodash';
import DashletContainer from 'Components/DashletContainer';
import clsx from 'clsx';
import { Icon } from '@brighterion/br-ui';
import { useTranslation, Trans } from 'react-i18next';
import { NumberService } from 'Services';
import SimulationSandboxData from '../SimulationSandboxData';
import RuleSimulationTable from './RuleSimulationTable';
import styles from './index.css';
import SimulationBarChart from './SimulationBarChart';

const RuleSimulationDashlet = ({
    formValues,
    className,
    chartType,
    filterView,
    sortView,
    setRejectedTransactionCount,
    setTotalTransactionCount,
    sandboxData,
    compareData
}: RuleSimulationProps) => {
    const { t } = useTranslation();
    const {
        isLoading,
        processedData,
        totalRejectedTransactions,
        totalRejectedMatches,
        deployedRejectedTransactions,
        deployedTotalRejectedMatches,
        totalSandBoxTransactions,
        totalDeployedTransactions
    } = SimulationSandboxData(chartType, filterView, sortView, formValues, sandboxData, compareData);
    const isRejectChart = chartType === 'reject';
    const maxBarAccessor = chartType === 'all' ? 'percentScaledTriggeredAll' : 'percentScaledTriggeredReject';
    const maxBarValue = useMemo(() => {
        const maxObj = maxBy(
            processedData,
            (simulationPerformanceTableItem) => simulationPerformanceTableItem[maxBarAccessor]
        );
        return maxObj && maxObj[maxBarAccessor] ? maxObj[maxBarAccessor] : 1;
    }, [processedData, maxBarAccessor]);
    const deployedMaxBarValue = useMemo(() => {
        const maxObj = maxBy(processedData, (processObject) => {
            return processObject?.performanceData?.[maxBarAccessor];
        });
        return maxObj && maxObj[maxBarAccessor] ? maxObj[maxBarAccessor] : 1;
    }, [processedData, maxBarAccessor]);
    const columns: Column<SimulationPerformanceTableItem>[] = useMemo(() => {
        const ruleColumn: Column<SimulationPerformanceTableItem> = {
            Header: 'Rule',
            accessor: 'rule',
            renderType: 'overrideCell',
            Cell: ({ value, row }: { value: number; row: Row }) => {
                return (
                    <span data-testid='rule-info' className={styles.rulesColumn}>
                        {value}
                        <span className={styles.rulesColumnSandbox}>
                            <span>
                                {row.original.modified && <Icon name='table-updated' size='sm' />}
                                {row.original.new && <Icon name='table-new' size='sm' />}
                            </span>
                            <span>{t('Sandbox')}</span>
                        </span>
                        {Object.keys(row.original.performanceData).length ? (
                            <span className={styles.rulesColumnRowText}>
                                <span>{t('Deployed')}</span>
                            </span>
                        ) : null}
                    </span>
                );
            }
        };

        const matchesColumn: Column<SimulationPerformanceTableItem> = {
            Header: 'Matches',
            renderType: 'overrideCell',
            accessor: 'matches',
            Cell: ({ value, row }: { value: number; row: Row }) => {
                return (
                    <span>
                        <span className={styles.rulesColumnRowText}>
                            <span>{value === 0 ? t('N/A') : value}</span>
                        </span>
                        {Object.keys(row.original.performanceData).length ? (
                            <span className={styles.rulesColumnRowText}>
                                <span>
                                    {row?.original?.performanceData?.matches === 0
                                        ? t('N/A')
                                        : row?.original?.performanceData?.matches}
                                </span>
                            </span>
                        ) : null}
                    </span>
                );
            }
        };
        const percentColumn: Column<SimulationPerformanceTableItem> = {
            Header: 'Percent Rule Triggered',
            accessor: chartType === 'all' ? 'percentScaledTriggeredAll' : 'percentScaledTriggeredReject',
            renderType: 'overrideCell',
            Cell: ({ value, row }: { value: number; row: Row }) => {
                return (
                    <>
                        <SimulationBarChart
                            row={row.original}
                            isRejectChart={isRejectChart}
                            totalRejectedMatches={totalRejectedMatches}
                            chartType={chartType}
                            value={value}
                            maxBarValue={maxBarValue}
                            progressBarFillAreaStyle={`${chartType}ProgressBarFillArea`}
                            totalSandBoxTransactions={totalSandBoxTransactions}
                            totalDeployedTransactions={totalDeployedTransactions}
                        />
                        {Object.keys(row?.original?.performanceData).length ? (
                            <SimulationBarChart
                                row={row?.original?.performanceData}
                                isRejectChart={isRejectChart}
                                totalRejectedMatches={deployedTotalRejectedMatches}
                                chartType={chartType}
                                value={row?.original?.performanceData[maxBarAccessor]}
                                maxBarValue={deployedMaxBarValue}
                                progressBarFillAreaStyle={`${chartType}DeployedProgressBarFillArea`}
                                totalSandBoxTransactions={totalSandBoxTransactions}
                                totalDeployedTransactions={totalDeployedTransactions}
                            />
                        ) : null}
                    </>
                );
            }
        };

        return [{ ...ruleColumn }, { ...matchesColumn }, { ...percentColumn }];
    }, [
        chartType,
        t,
        deployedMaxBarValue,
        deployedTotalRejectedMatches,
        isRejectChart,
        maxBarAccessor,
        maxBarValue,
        totalRejectedMatches,
        totalSandBoxTransactions,
        totalDeployedTransactions
    ]);

    React.useEffect(() => {
        if (!isLoading && setRejectedTransactionCount) {
            setRejectedTransactionCount(totalRejectedTransactions);
        }
        if (!isLoading && setTotalTransactionCount) {
            setTotalTransactionCount(totalSandBoxTransactions || totalDeployedTransactions);
        }
    }, [
        isLoading,
        setRejectedTransactionCount,
        setTotalTransactionCount,
        totalRejectedTransactions,
        totalSandBoxTransactions,
        totalDeployedTransactions
    ]);

    const dashletContainerCN = clsx(className, styles.container);
    const testId = `rule-performance-${chartType}`;

    const subHeader = (
        <>
            <Trans i18nKey='ruleSimulationSubHeader'>
                Rules triggered:
                {{ chartType: isRejectChart ? 'Rejected' : 'All' }}
            </Trans>
        </>
    );

    const showTotalData = () => {
        if (isRejectChart) {
            return deployedRejectedTransactions
                ? t('compareTransactions', {
                      sandboxTransaction: NumberService.getLocaleNumber(totalRejectedTransactions),
                      deployedTransaction: NumberService.getLocaleNumber(deployedRejectedTransactions)
                  })
                : t('transactions', { number: NumberService.getLocaleNumber(totalRejectedTransactions) });
        }
        return totalDeployedTransactions
            ? t('compareTransactions', {
                  sandboxTransaction: NumberService.getLocaleNumber(totalSandBoxTransactions),
                  deployedTransaction: NumberService.getLocaleNumber(totalDeployedTransactions)
              })
            : t('transactions', { number: NumberService.getLocaleNumber(totalSandBoxTransactions) });
    };

    return (
        <DashletContainer
            dataTestId={testId}
            className={dashletContainerCN}
            isLoading={isLoading}
            header={{
                label: isRejectChart ? t('ruleSignificanceTitle') : t('triggerFrequencyTitle'),
                toolTip: isRejectChart ? t('ruleSignificanceTooltip') : t('triggerFrequencyTooltip')
            }}
            subHeader={subHeader}
            descriptionAboveChildren={showTotalData()}
        >
            <RuleSimulationTable
                data={processedData}
                columns={columns}
                dataTestId={`${testId}-table`}
                noDataMessage={t('ruleSimulationDataIsNotFound')}
            />
        </DashletContainer>
    );
};

export default React.memo(RuleSimulationDashlet);
